/**
 * ``StatusStreamSections`` — the **client-observed** live-stream states on the
 * User Interface Manager Selection Detail (REQ-STATUS-049).
 *
 * The top bar used to carry one connection indicator. REQ-STATUS-048 took that
 * slot for the server-reported system rollup badge, and architect ruling 8
 * (2026-07-31) re-homed the connection state here — which is semantically where
 * it belongs, because the BFF *is* the User Interface Manager and it serves both
 * streams.
 *
 * Two qualifications the ruling attaches, both load-bearing:
 *
 * 1. **Both streams are shown, separately.** ``/api/query/sse`` (map) and
 *    ``/api/stream/system-status`` are independent :class:`EventSource`s with
 *    independent failure modes. Reporting one as "the connection" would let a
 *    dead map feed hide behind a healthy status feed, or the reverse — the
 *    single worst outcome for a surface whose whole job is answering "is my
 *    data still moving?".
 * 2. **The section is explicitly marked client-observed.** Every other field in
 *    this panel is a collector-gathered fact about the deployment; these two are
 *    facts about one browser tab. Unlabelled, "RECONNECTING" here reads as a
 *    statement about the system rather than about this laptop's connection —
 *    which is exactly the confusion that put the indicator in the top bar's
 *    system slot in the first place.
 *
 * # On the map stream's ``idle`` state
 *
 * ``idle`` means the Map panel has published no viewport (it is closed,
 * undocked, or not yet ready), so no subscription exists. It is deliberately
 * distinct from ``reconnecting``: nothing is being retried. Until the
 * REQ-STATUS-049 prerequisite fix landed in :file:`@/map/use-sse`, this state
 * was reported as ``reconnecting``, so every workspace without the Map panel
 * showed a permanent amber — the field would have shipped lying.
 *
 * Cross-references:
 *   - REQ-STATUS-049 (client-observed stream states); REQ-STATUS-048 (the badge
 *     that took the top-bar slot); REQ-STATUS-062 (the ``closed`` state; as
 *     amended 2026-10-19, the ``unavailable`` state).
 *   - REQ-UI-008 (live data stream indicator); REQ-UI-046 (never colour alone);
 *     REQ-UI-059 (landmarks + accessible names).
 *   - ADR-024 §4 (consolidated Selection Detail, sticky sections).
 *   - docs/engineering/detail/storyboards/flows/A-sse-recovery/README.md.
 */
import { DescriptionTooltip } from "@/components/ui/tooltip";
import { SectionShell } from "@/entity-details/section-shell";
import { useSseConnection, useSseState } from "@/map/sse-context";
import type { SseConnectionState } from "@/map/use-sse";
import {
  useSystemStatusStreamState,
  type StatusStreamState,
} from "@/system-status/use-system-status-stream";
import { cn } from "@/lib/utils";

/**
 * The section id. Stable and independent of the visible title (REQ-STATUS-027
 * defect (c): a label-derived id retargets every selector at once when the
 * wording changes, and a suite whose selectors match nothing goes green).
 */
export const STREAM_SECTION_ID = "ui-streams";

/** The map stream's endpoint, restated for the operator-facing row. */

/** One row's presentation: state word, dot class, and an optional detail note. */
interface StreamStatePresentation {
  label: string;
  dotClass: string;
  detail?: string;
}

/**
 * Map-stream state presentation.
 *
 * ``fail-stop`` keeps the ``DISCONNECTED`` word it has always carried (Flow A:
 * the two states are visually identical at indicator level, the difference
 * being the modal overlay layered on top), but gains a detail note — in a
 * diagnostic pane, silently collapsing "dropped" into "dropped for over five
 * minutes" throws away the one fact an on-call engineer wants (CLAUDE.md §5.4).
 */
const MAP_STREAM_PRESENTATION: Record<SseConnectionState, StreamStatePresentation> = {
  idle: {
    label: "IDLE",
    dotClass: "bg-decor-muted",
    detail: "Not subscribed — the Map panel has published no viewport.",
  },
  live: { label: "LIVE", dotClass: "bg-meridian-status-live" },
  reconnecting: {
    label: "RECONNECTING",
    dotClass: "bg-meridian-status-reconnecting",
    detail: "Dropped; the browser is retrying.",
  },
  disconnected: {
    label: "DISCONNECTED",
    dotClass: "bg-meridian-status-disconnected",
    detail: "No recovery for over 30 seconds.",
  },
  "fail-stop": {
    label: "DISCONNECTED",
    dotClass: "bg-meridian-status-disconnected",
    detail: "No recovery for over 5 minutes.",
  },
  paused: {
    label: "PAUSED",
    dotClass: "bg-meridian-status-paused",
    detail: "Paused by the operator.",
  },
};

/**
 * REQ-UI-197: a server-side drop is shown to the operator with its count. The
 * BFF's per-client queue announces every frame it discards for a slow
 * browser (``meridian-dropped``); the map row carries the cumulative count
 * for this connection as a "feed behind" detail, whatever the state word —
 * a stream can be LIVE and still have lost frames, which is exactly the fact
 * an operator cannot otherwise see (CLAUDE.md §5.4, §8.1).
 */
function withFeedBehind(
  presentation: StreamStatePresentation,
  droppedFrames: number,
): StreamStatePresentation {
  if (droppedFrames <= 0) {
    return presentation;
  }
  const noun = droppedFrames === 1 ? "frame" : "frames";
  const note = `Feed behind — the server dropped ${droppedFrames.toLocaleString()} ${noun} for this browser on this connection.`;
  return {
    ...presentation,
    detail: presentation.detail === undefined ? note : `${presentation.detail} ${note}`,
  };
}

/**
 * System-status-stream state presentation.
 *
 * Neither terminal state is amber. ``closed`` means the browser stopped
 * retrying and the hook's probe confirmed an auth rejection (401/403 once the
 * session expires — REQ-STATUS-062); ``unavailable`` means it stopped for any
 * other reason and the hook's bounded reopen ran out (REQ-STATUS-062 amended
 * 2026-10-19). Calling either "reconnecting" would promise a recovery that is
 * not coming, and giving ``unavailable`` the ``closed`` instruction would send
 * an operator with a valid session to the login page.
 */
const STATUS_STREAM_PRESENTATION: Record<StatusStreamState, StreamStatePresentation> = {
  connecting: { label: "CONNECTING", dotClass: "bg-decor-muted" },
  live: { label: "LIVE", dotClass: "bg-meridian-status-live" },
  reconnecting: {
    label: "RECONNECTING",
    dotClass: "bg-meridian-status-reconnecting",
    detail: "Dropped; retrying.",
  },
  closed: {
    label: "CLOSED",
    dotClass: "bg-meridian-status-disconnected",
    detail: "Stopped retrying — sign in again to resume.",
  },
  unavailable: {
    label: "UNAVAILABLE",
    dotClass: "bg-meridian-status-disconnected",
    detail: "Stopped retrying after 5 minutes — reload the page to try again.",
  },
};

/**
 * One stream row: name, endpoint, state word + dot, optional detail.
 *
 * ``testId`` is required and decoupled from the label for the same reason
 * :func:`MiniStateRow`'s is.
 */
function StreamStateRow({
  testId,
  name,
  description,
  presentation,
  stateToken,
  dropped,
}: {
  testId: string;
  name: string;
  /** Plain-language explanation, disclosed through the shared tooltip primitive. */
  description: string;
  presentation: StreamStatePresentation;
  /** The raw state token, published as ``data-state`` for test + diagnosis. */
  stateToken: string;
  /** Frames the server dropped on this connection (REQ-UI-197), published as ``data-dropped``. */
  dropped?: number;
}): React.ReactElement {
  return (
    <div
      className="py-0.5 text-body-sm"
      data-testid={testId}
      data-state={stateToken}
      data-dropped={dropped ?? 0}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden="true"
          className={cn("inline-block h-2 w-2 shrink-0 rounded-full", presentation.dotClass)}
        />
        <span className="text-text-primary">{name}</span>
        <DescriptionTooltip testId={testId} label={name} description={description} />
        {/* The state WORD, not the dot alone, carries the state (REQ-UI-046). */}
        <span className="ml-auto font-mono uppercase tracking-wide text-text-primary">
          {presentation.label}
        </span>
      </div>
      {/* The endpoint PATH that stood here is removed (architect, 2026-08-01) — an
          operator cannot act on `/api/query/sse`, and it read as leaked plumbing. The
          state DETAIL stays: "Not subscribed" / "Stopped retrying" is what
          distinguishes states that would otherwise differ only by dot colour, which
          REQ-UI-046 forbids, and it is the sentence that tells an operator whether a
          non-live stream is a fault or expected. */}
      {presentation.detail !== undefined && (
        <p className="pl-4 text-meta text-text-secondary">{presentation.detail}</p>
      )}
    </div>
  );
}

/**
 * Render the two client-observed stream states for the User Interface Manager
 * Selection Detail (REQ-STATUS-049).
 *
 * Reads both application-scope providers; renders nothing of its own about the
 * deployment. Exported for mounting from the ``status-component`` Selection
 * Detail when the selected component is the BFF / User Interface Manager.
 */
export function StatusStreamSections(): React.ReactElement {
  const mapState = useSseState();
  const droppedFrames = useSseConnection().droppedFrames;
  const statusState = useSystemStatusStreamState();

  return (
    <SectionShell id={STREAM_SECTION_ID} title="Live streams (this browser)">
      <div data-testid="status-stream-sections">
        {/* The explanatory paragraph that stood here is removed (architect,
            2026-08-01) — a section that has to justify its own existence in prose is
            in the wrong register for an operator surface. The client-observed
            distinction it carried is REAL and must not be lost with it: everything
            else on this display is collector-gathered server telemetry, so an
            unmarked "RECONNECTING" here would read as a claim about the deployment.
            That distinction now lives in the section heading ("this browser") and in
            each row's tooltip, where a reader meets it at the point of confusion
            rather than as a preamble. */}
        <StreamStateRow
          testId="stream-state-map"
          name="Map data stream"
          description="Connection status for the map display data stream in this browser."
          presentation={withFeedBehind(MAP_STREAM_PRESENTATION[mapState], droppedFrames)}
          stateToken={mapState}
          dropped={droppedFrames}
        />
        <StreamStateRow
          testId="stream-state-system-status"
          name="System status stream"
          description="Connection status for the system display data stream in this browser."
          presentation={STATUS_STREAM_PRESENTATION[statusState]}
          stateToken={statusState}
        />
      </div>
    </SectionShell>
  );
}
